import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ChevronLeft, ExternalLink, PanelRightClose, PanelRightOpen, RotateCcw, Save } from "lucide-react";
import ProductLivePreview from "../components/ProductLivePreview";
import { useProductPreviewEdit } from "../components/productEditBridge";
import { useSaveShortcut, saveKeyLabel } from "../useSaveShortcut";
import { rhm, apiError, money, dt, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, PageHead, Select, Table, Td, Textarea, statusTone } from "../ui";
import { ListEditor, RowEditor } from "../editors";
import { LiveSwitch } from "../components/LiveSwitch";
import { CoverImageField, uploadDigitalFile } from "../components/EbookAssets";
import FormulationEditor from "../FormulationEditor";

const TABS = ["overview", "commercial", "formulation", "inventory", "batches", "website", "shop", "blogs", "automation", "activity"];
// Ebooks and consultation packs have no stock, batches or formulation.
const DIGITAL_TABS = ["overview", "commercial", "shop", "website", "blogs", "activity"];
const tabsFor = (shopType) => (shopType && shopType !== "product" ? DIGITAL_TABS : TABS);
const KIND_LABEL = { consultation_pack: "Consultation Pack", ebook: "Ebook / Guide" };
const MAX_GUIDE_LABEL = "100 MB";
const num = (v) => (v === "" || v === null ? null : Number(v));

// One payload per tab, so the SAVE button and the keyboard shortcut always send the same thing.
const payloadFor = (tab, d) => {
    if (tab === "overview") {
        return {
            name: d.name, slug: d.slug, product_code: d.product_code, sku: d.sku,
            division: d.division, product_type: d.product_type, status: d.status,
            pack_size: d.pack_size, unit_label: d.unit_label, manufacturer: d.manufacturer,
            short_description: d.short_description, description: d.description,
            composition: (d.composition || []).filter((c) => c.ingredient),
            images: d.images,
            concerns: d.concerns || [],
            rating_manual: Number(d.rating_manual || 0),
            rating_manual_count: Number(d.rating_manual_count || 0),
        };
    }
    if (tab === "commercial") {
        return {
            mrp: num(d.mrp) ?? 0, selling_price: num(d.selling_price),
            purchase_price: num(d.purchase_price), gst: num(d.gst) ?? 18,
            min_stock: num(d.min_stock) ?? 0, reorder_qty: num(d.reorder_qty) ?? 0,
            consumption_period_days: num(d.consumption_period_days) ?? 30,
            discount_eligible: !!d.discount_eligible, offer_eligible: !!d.offer_eligible,
        };
    }
    if (tab === "formulation") return { formulation: d.formulation || {} };
    if (tab === "website") return { website: d.website };
    return null;
};

export default function RhmProductWorkspace() {
    const { productId } = useParams();
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [tab, setTab] = useState("overview");
    const [draft, setDraft] = useState(null);
    const [showPreview, setPreview] = useState(true);

    const { data: product, isLoading } = useQuery({
        queryKey: ["rhm-product", productId],
        queryFn: async () => (await rhm.get(`/products/${productId}`)).data,
    });
    const { data: meta } = useQuery({
        queryKey: ["rhm-product-meta"],
        queryFn: async () => (await rhm.get("/product-meta")).data,
    });

    useEffect(() => { if (product) setDraft(product); }, [product]);

    const editable = can("products.edit");
    const pv = useProductPreviewEdit({ productId, draft, setDraft, editable });

    const save = useMutation({
        mutationFn: async (payload) => (await rhm.patch(`/products/${productId}`, payload)).data,
        onSuccess: () => {
            toast.success("Product updated");
            qc.invalidateQueries({ queryKey: ["rhm-product", productId] });
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    // Live / Off switch — takes the product page off the website without changing anything else.
    const setLive = useMutation({
        mutationFn: async (live) => (await rhm.patch(`/products/${productId}/quick`, { page_live: live })).data,
        onSuccess: (_d, live) => {
            toast.success(live ? "Product page is live again" : "Product page switched off");
            qc.invalidateQueries({ queryKey: ["rhm-product", productId] });
            qc.invalidateQueries({ queryKey: ["rhm-products"] });
        },
        onError: (e) => toast.error(apiError(e)),
    });

    // Ctrl/Cmd + S (and Ctrl/Cmd + Enter) saves the tab you are on — no scrolling back up.
    useSaveShortcut(() => {
        if (!draft) return;
        if (!editable) {
            toast.error("You do not have permission to edit this product");
            return;
        }
        if (pv.dirty) pv.save();
        const body = payloadFor(tab, draft);
        if (body) save.mutate(body);
        else if (!pv.dirty) toast.message("Nothing to save on this tab");
    });

    if (isLoading || !draft) return <div className="mono text-xs text-zinc-500">LOADING PRODUCT…</div>;


    const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
    const setWeb = (k, v) => setDraft((d) => ({ ...d, website: { ...(d.website || {}), [k]: v } }));
    const saveTab = () => save.mutate(payloadFor(tab, draft));
    const isMac = saveKeyLabel() === "⌘";

    return (
        <div data-testid="rhm-product-workspace">
            <Link to="/rhm/products" className="inline-flex items-center gap-1.5 mono text-[10px] tracking-[0.16em] text-zinc-500 hover:text-gold mb-4">
                <ChevronLeft className="w-3.5 h-3.5" /> ALL PRODUCTS
            </Link>

            <PageHead
                title={product.name}
                sub={`${product.product_code} · ${product.division} · ${product.pack_size || product.product_type}`}
                action={<div className="flex items-center gap-3">
                    <LiveSwitch live={product.page_live !== false} disabled={!editable || setLive.isPending}
                        testId="ws-live-switch" previewHref={`/product/${product.slug}`}
                        onChange={(live) => setLive.mutate(live)} />
                    <button type="button" onClick={() => setPreview((v) => !v)} data-testid="ws-toggle-preview"
                        className="mono text-[10px] tracking-[0.16em] text-zinc-400 hover:text-gold transition-colors inline-flex items-center gap-1.5">
                        {showPreview ? <PanelRightClose className="w-3.5 h-3.5" /> : <PanelRightOpen className="w-3.5 h-3.5" />}
                        {showPreview ? "HIDE PREVIEW" : "LIVE PREVIEW"}
                    </button>
                    <a href={`/product/${product.slug}`} target="_blank" rel="noreferrer"
                        data-testid="product-view-live"
                        className="mono text-[10px] tracking-[0.16em] text-lime hover:text-gold transition-colors inline-flex items-center gap-1.5">
                        <ExternalLink className="w-3.5 h-3.5" /> VIEW ON WEBSITE
                    </a>
                    {KIND_LABEL[product.shop_type] && (
                        <Badge tone="info" data-testid="product-kind-badge">{KIND_LABEL[product.shop_type]}</Badge>
                    )}
                    <Badge tone={statusTone(product.status)}>{titleCase(product.status || "")}</Badge>
                    {(product.shop_type || "product") === "product" && (
                        <Badge tone={product.stock?.available <= (product.min_stock || 0) ? "danger" : "good"}>
                            {product.stock?.available ?? 0} {product.unit_label}s in stock
                        </Badge>
                    )}
                </div>}
            />

            <div className="flex flex-wrap items-center gap-1 border-b border-white/10 mb-5">
                {tabsFor(product.shop_type).map((t) => (
                    <button key={t} onClick={() => setTab(t)} data-testid={`rhm-tab-${t}`}
                        className={`mono text-[10px] tracking-[0.16em] px-4 py-3 border-b-2 -mb-px transition-colors ${
                            tab === t ? "border-gold text-gold" : "border-transparent text-zinc-500 hover:text-white"}`}>
                        {t.toUpperCase()}
                    </button>
                ))}
                <span className="ml-auto mono text-[9px] tracking-[0.16em] text-zinc-600 pr-2 hidden sm:block"
                    data-testid="ws-save-hint">
                    {isMac ? "⌘" : "CTRL"} + S SAVES THIS TAB
                </span>
            </div>

            <div className={`grid gap-5 ${showPreview
                ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,46%)]" : "grid-cols-1"}`}>
            <div className="min-w-0 space-y-5">
            {tab === "overview" && (
                <Card title="Product Information" action={editable && (
                    <Btn onClick={saveTab} data-testid="rhm-save-overview">SAVE</Btn>
                )}>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Field label="Product name"><Input disabled={!editable} value={draft.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="ws-name" /></Field>
                        <Field label="Product code"><Input disabled={!editable} value={draft.product_code || ""} onChange={(e) => set("product_code", e.target.value.toUpperCase())} /></Field>
                        <Field label="Web address"
                            hint="Change it and old links redirect here automatically">
                            <div className="flex items-center gap-1">
                                <span className="mono text-[11px] text-zinc-500 shrink-0">/product/</span>
                                <Input disabled={!editable} value={draft.slug || ""} data-testid="product-slug"
                                    className="mono !text-[12px]"
                                    onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} />
                            </div>
                        </Field>
                        <Field label="SKU"><Input disabled={!editable} value={draft.sku || ""} onChange={(e) => set("sku", e.target.value.toUpperCase())} /></Field>
                        <Field label="Division"><Select disabled={!editable} value={draft.division || ""} onChange={(e) => set("division", e.target.value)} options={meta?.divisions || []} /></Field>
                        <Field label="Product type"><Input disabled={!editable} value={draft.product_type || ""} onChange={(e) => set("product_type", e.target.value)} /></Field>
                        <Field label="Status" hint="Coming Soon shows Notify Me on the website">
                            <Select disabled={!editable} value={draft.status || "draft"} onChange={(e) => set("status", e.target.value)} options={meta?.statuses || []} data-testid="ws-status" />
                        </Field>
                        <Field label="Pack size"><Input disabled={!editable} value={draft.pack_size || ""} onChange={(e) => set("pack_size", e.target.value)} /></Field>
                        <Field label="Unit label"><Input disabled={!editable} value={draft.unit_label || ""} onChange={(e) => set("unit_label", e.target.value)} /></Field>
                        <Field label="Manufacturer"><Input disabled={!editable} value={draft.manufacturer || ""} onChange={(e) => set("manufacturer", e.target.value)} /></Field>
                        <Field label="Short description" className="md:col-span-3"><Input disabled={!editable} value={draft.short_description || ""} onChange={(e) => set("short_description", e.target.value)} /></Field>
                        <Field label="Full description" className="md:col-span-3"><Textarea rows={4} disabled={!editable} value={draft.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>
                    </div>

                    <div className="mt-7">
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">COMPOSITION</div>
                        <RowEditor testId="ws-composition" value={draft.composition || []} onChange={(v) => set("composition", v)}
                            fields={[
                                { key: "ingredient", label: "Ingredient" },
                                { key: "strength", label: "Strength / quantity" },
                                { key: "unit", label: "Unit" },
                                { key: "note", label: "Note", wide: true },
                            ]} />
                    </div>
                </Card>
            )}

            {tab === "overview" && (
                <RatingsConcernsCard draft={draft} set={set} editable={editable} saveTab={saveTab}
                    product={product} />
            )}

            {tab === "commercial" && (
                <Card title="Commercial & Reorder Settings" action={editable && (
                    <Btn onClick={saveTab} data-testid="rhm-save-commercial">SAVE</Btn>
                )}>
                    <div className="grid md:grid-cols-3 gap-4">
                        <Field label="MRP (₹)"><Input type="number" step="0.01" min="0" disabled={!editable} value={draft.mrp ?? ""} onChange={(e) => set("mrp", e.target.value)} data-testid="ws-mrp" /></Field>
                        <Field label="Selling price (₹)" hint="Offers are applied on top at checkout">
                            <Input type="number" step="0.01" min="0" disabled={!editable} value={draft.selling_price ?? ""} onChange={(e) => set("selling_price", e.target.value)} data-testid="ws-selling" />
                        </Field>
                        <Field label="Purchase price (₹)" hint="Left blank until entered">
                            <Input type="number" step="0.01" min="0" disabled={!editable} value={draft.purchase_price ?? ""} onChange={(e) => set("purchase_price", e.target.value)} data-testid="ws-purchase" />
                        </Field>
                        <Field label="GST %"><Input type="number" min="0" disabled={!editable} value={draft.gst ?? ""} onChange={(e) => set("gst", e.target.value)} /></Field>
                        <Field label="Minimum stock level"><Input type="number" min="0" disabled={!editable} value={draft.min_stock ?? ""} onChange={(e) => set("min_stock", e.target.value)} data-testid="ws-min-stock" /></Field>
                        <Field label="Reorder quantity"><Input type="number" min="0" disabled={!editable} value={draft.reorder_qty ?? ""} onChange={(e) => set("reorder_qty", e.target.value)} data-testid="ws-reorder-qty" /></Field>
                        <Field label="Consumption period (days)"><Input type="number" min="1" disabled={!editable} value={draft.consumption_period_days ?? ""} onChange={(e) => set("consumption_period_days", e.target.value)} /></Field>
                        <Field label="Discount eligible">
                            <Select disabled={!editable} value={draft.discount_eligible ? "yes" : "no"} onChange={(e) => set("discount_eligible", e.target.value === "yes")}
                                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                        </Field>
                        <Field label="Offer eligible">
                            <Select disabled={!editable} value={draft.offer_eligible ? "yes" : "no"} onChange={(e) => set("offer_eligible", e.target.value === "yes")}
                                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]} />
                        </Field>
                    </div>
                    <div className="mt-5 grid md:grid-cols-4 gap-4 text-sm">
                        <div className="border border-white/10 p-4">
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">LIVE WEBSITE PRICE</div>
                            <div className="font-display text-2xl mt-1 text-gold">{money(product.price)}</div>
                        </div>
                        <div className="border border-white/10 p-4">
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">STRIKE-THROUGH</div>
                            <div className="font-display text-2xl mt-1">{money(product.compare_at_price)}</div>
                        </div>
                        <div className="border border-white/10 p-4">
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">PURCHASABLE</div>
                            <div className="font-display text-2xl mt-1">{product.purchasable ? "YES" : "NO"}</div>
                        </div>
                        <div className="border border-white/10 p-4">
                            <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">GST</div>
                            <div className="font-display text-2xl mt-1">{product.gst ?? 0}%</div>
                        </div>
                    </div>
                </Card>
            )}

            {tab === "formulation" && (
                <Card title="Formulation — Combos, Actives & Usage" action={editable && (
                    <Btn onClick={saveTab} data-testid="rhm-save-formulation">SAVE</Btn>
                )}>
                    <FormulationEditor
                        value={draft.formulation || {}}
                        editable={editable}
                        onChange={(v) => set("formulation", v)}
                    />
                </Card>
            )}

            {tab === "inventory" && (
                <div className="grid md:grid-cols-2 gap-5">
                    <Card title="Stock Position">
                        <div className="grid grid-cols-2 gap-4">
                            {[["On hand", product.stock?.on_hand], ["Available", product.stock?.available],
                              ["Reserved", product.stock?.reserved], ["Sold", product.stock?.sold],
                              ["Damaged", product.stock?.damaged], ["Returned", product.stock?.returned]].map(([l, v]) => (
                                <div key={l} className="border border-white/10 p-4">
                                    <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">{l.toUpperCase()}</div>
                                    <div className="font-display text-2xl mt-1">{v ?? 0}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-4 text-sm text-zinc-400">
                            Minimum stock <span className="text-white font-bold">{product.min_stock}</span> ·
                            reorder <span className="text-white font-bold">{product.reorder_qty}</span> {product.unit_label}s
                        </div>
                        <Link to="/rhm/inventory" className="mt-4 inline-block mono text-[10px] tracking-[0.16em] text-gold">
                            OPEN STOCK MANAGER →
                        </Link>
                    </Card>
                    <Card title="Stock Ledger">
                        <Table head={["When", "Type", "Change", "After", "By"]}
                            empty={!product.ledger?.length && <Empty msg="No movements yet." />}>
                            {(product.ledger || []).map((l) => (
                                <tr key={l.id}>
                                    <Td className="text-xs text-zinc-500 whitespace-nowrap">{dt(l.created_at)}</Td>
                                    <Td><Badge>{titleCase(l.movement_type)}</Badge></Td>
                                    <Td className={l.quantity_change < 0 ? "text-red-400" : "text-lime"}>
                                        {l.quantity_change > 0 ? "+" : ""}{l.quantity_change}
                                    </Td>
                                    <Td>{l.quantity_after}</Td>
                                    <Td className="text-xs text-zinc-500">{l.user_name}</Td>
                                </tr>
                            ))}
                        </Table>
                    </Card>
                </div>
            )}

            {tab === "batches" && (
                <Card title="Batches" action={<Link to="/rhm/batches" className="mono text-[10px] tracking-[0.16em] text-gold">MANAGE →</Link>}>
                    <Table head={["Batch", "Mfg", "Expiry", "Quantity", "Available", "Status"]}
                        empty={!product.batches?.length && <Empty msg="No batches yet." />}>
                        {(product.batches || []).map((b) => (
                            <tr key={b.id}>
                                <Td className="mono text-xs">{b.batch_number || <span className="text-zinc-600">unnumbered</span>}</Td>
                                <Td className="text-sm">{b.mfg_date ? dOnly(b.mfg_date) : <span className="text-zinc-600">—</span>}</Td>
                                <Td className="text-sm">{b.expiry_date ? dOnly(b.expiry_date) : <span className="text-zinc-600">—</span>}</Td>
                                <Td>{b.quantity}</Td>
                                <Td>{b.available}</Td>
                                <Td><Badge tone={statusTone(b.status)}>{titleCase(b.status)}</Badge></Td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            )}

            {tab === "website" && (
                <Card title="Website Content" action={editable && (
                    <Btn onClick={saveTab} data-testid="rhm-save-website">SAVE</Btn>
                )}>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Headline"><Input disabled={!editable} value={draft.website?.headline || ""} onChange={(e) => setWeb("headline", e.target.value)} data-testid="ws-headline" /></Field>
                        <Field label="Tagline"><Input disabled={!editable} value={draft.website?.tagline || ""} onChange={(e) => setWeb("tagline", e.target.value)} /></Field>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">KEY BENEFITS</div>
                            <ListEditor value={draft.website?.key_benefits || []} onChange={(v) => setWeb("key_benefits", v)} testId="ws-benefits" />
                        </div>
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">PRODUCT HIGHLIGHTS</div>
                            <ListEditor value={draft.website?.highlights || []} onChange={(v) => setWeb("highlights", v)} />
                        </div>
                    </div>
                    <div className="mt-6">
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">FEATURE SECTIONS</div>
                        <RowEditor value={draft.website?.feature_sections || []} onChange={(v) => setWeb("feature_sections", v)}
                            fields={[{ key: "title", label: "Title" }, { key: "body", label: "Body", type: "textarea", wide: true }]} />
                    </div>
                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                        <Field label="Ingredient explanation" className="md:col-span-2">
                            <Textarea rows={3} disabled={!editable} value={draft.website?.ingredient_explanation || ""} onChange={(e) => setWeb("ingredient_explanation", e.target.value)} />
                        </Field>
                        <Field label="Powerful combo — title"><Input disabled={!editable} value={draft.website?.combo_title || ""} onChange={(e) => setWeb("combo_title", e.target.value)} /></Field>
                        <Field label="Powerful combo — body"><Input disabled={!editable} value={draft.website?.combo_body || ""} onChange={(e) => setWeb("combo_body", e.target.value)} /></Field>
                    </div>
                    <div className="mt-4">
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">COMBO ITEMS</div>
                        <RowEditor value={draft.website?.combo_items || []} onChange={(v) => setWeb("combo_items", v)}
                            fields={[{ key: "title", label: "Item" }, { key: "detail", label: "Detail", wide: true }]} />
                    </div>
                    <div className="mt-6 grid md:grid-cols-3 gap-4">
                        <Field label="How to use"><Textarea rows={3} disabled={!editable} value={draft.website?.usage || ""} onChange={(e) => setWeb("usage", e.target.value)} /></Field>
                        <Field label="Directions"><Textarea rows={3} disabled={!editable} value={draft.website?.directions || ""} onChange={(e) => setWeb("directions", e.target.value)} /></Field>
                        <Field label="Warnings"><Textarea rows={3} disabled={!editable} value={draft.website?.warnings || ""} onChange={(e) => setWeb("warnings", e.target.value)} /></Field>
                    </div>
                    <div className="mt-6 grid md:grid-cols-2 gap-4">
                        <Field label="Signature line (under How to use)">
                            <Input disabled={!editable} value={draft.website?.signature_line || ""}
                                onChange={(e) => setWeb("signature_line", e.target.value)} data-testid="ws-signature-line" />
                        </Field>
                        <Field label="The Science (product page accordion)">
                            <Textarea rows={3} disabled={!editable} value={draft.website?.science_text || ""}
                                onChange={(e) => setWeb("science_text", e.target.value)} data-testid="ws-science-text" />
                        </Field>
                    </div>
                    <div className="mt-8 border-t border-white/10 pt-6">
                        <div className="mono text-[10px] tracking-[0.2em] text-gold mb-1">SEARCH APPEARANCE</div>
                        <p className="text-[11px] text-zinc-500 mb-4">
                            How this product reads on Google. Aim for a 50–60 character title and a
                            120–160 character description; leave blank to fall back to the name and tagline.
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label={`Search title (${(draft.website?.seo?.seo_title || "").length}/60)`}>
                                <Input disabled={!editable} value={draft.website?.seo?.seo_title || ""}
                                    data-testid="ws-seo-title"
                                    onChange={(e) => setWeb("seo", { ...(draft.website?.seo || {}), seo_title: e.target.value })} />
                            </Field>
                            <Field label="Share image (optional)">
                                <Input disabled={!editable} value={draft.website?.seo?.social_image || ""}
                                    data-testid="ws-seo-image"
                                    onChange={(e) => setWeb("seo", { ...(draft.website?.seo || {}), social_image: e.target.value })} />
                            </Field>
                            <Field label={`Search description (${(draft.website?.seo?.meta_description || "").length}/160)`}
                                className="md:col-span-2">
                                <Textarea rows={2} disabled={!editable} value={draft.website?.seo?.meta_description || ""}
                                    data-testid="ws-seo-description"
                                    onChange={(e) => setWeb("seo", { ...(draft.website?.seo || {}), meta_description: e.target.value })} />
                            </Field>
                        </div>
                        <div className="mt-4 p-4 bg-white/5 rounded" data-testid="ws-seo-preview">
                            <div className="text-[11px] text-zinc-500">rehealthme.com › product › {draft.slug || "…"}</div>
                            <div className="text-[#8ab4f8] text-base mt-1">
                                {draft.website?.seo?.seo_title || `${draft.name || "Product"} — ${draft.website?.tagline || "ReHealthMe"}`}
                            </div>
                            <div className="text-xs text-zinc-400 mt-1">
                                {draft.website?.seo?.meta_description || draft.website?.tagline || "No description yet"}
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 grid md:grid-cols-2 gap-6">
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">FAQS</div>
                            <RowEditor value={draft.website?.faqs || []} onChange={(v) => setWeb("faqs", v)}
                                fields={[{ key: "q", label: "Question", wide: true }, { key: "a", label: "Answer", type: "textarea", wide: true }]} />
                        </div>
                        <div>
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">SPECIFICATIONS</div>
                            <RowEditor value={draft.website?.specifications || []} onChange={(v) => setWeb("specifications", v)}
                                fields={[{ key: "label", label: "Label" }, { key: "value", label: "Value" }]} />
                            <div className="mono text-[10px] tracking-[0.2em] text-gold mt-6 mb-3">SUITABLE FOR</div>
                            <ListEditor value={draft.website?.suitable_for || []} onChange={(v) => setWeb("suitable_for", v)} />
                        </div>
                    </div>
                </Card>
            )}

            {tab === "shop" && <ShopListingCard product={product} productId={productId} editable={editable} />}

            {tab === "blogs" && <RelatedBlogs slug={product.slug} />}

            {tab === "automation" && (
                <Card title="Reorder & Notification Rules">
                    <div className="text-sm text-zinc-400 space-y-3">
                        <p>Low stock alert fires when available stock reaches <span className="text-white font-bold">{product.min_stock} {product.unit_label}s</span>,
                            with a suggested reorder of <span className="text-white font-bold">{product.reorder_qty} {product.unit_label}s</span>.</p>
                        <p>Customer reorder reminders are scheduled <span className="text-white font-bold">{product.consumption_period_days} days</span> after delivery.</p>
                        <p className="text-zinc-500">Expiry alert thresholds are configured globally in Settings. Channel templates (email / WhatsApp) arrive with the automation module.</p>
                    </div>
                </Card>
            )}

            {tab === "activity" && (
                <Card title="Activity & Audit Trail">
                    <Table head={["When", "User", "Action", "Before", "After"]}
                        empty={!product.activity?.length && <Empty msg="No changes recorded yet." />}>
                        {(product.activity || []).map((a) => (
                            <tr key={a.id}>
                                <Td className="text-xs text-zinc-500 whitespace-nowrap">{dt(a.created_at)}</Td>
                                <Td className="text-sm">{a.user_name}</Td>
                                <Td><Badge>{titleCase(a.action)}</Badge></Td>
                                <Td className="text-xs text-zinc-500 max-w-[220px] truncate">{a.before ? JSON.stringify(a.before) : "—"}</Td>
                                <Td className="text-xs text-zinc-400 max-w-[220px] truncate">{a.after ? JSON.stringify(a.after) : "—"}</Td>
                            </tr>
                        ))}
                    </Table>
                </Card>
            )}
            </div>
            {showPreview && (
                <div className="min-w-0 xl:sticky xl:top-24 self-start" data-testid="ws-preview-pane">
                    <div className="flex flex-wrap items-center justify-between gap-3 border border-white/10 bg-bg1 px-3 py-2 mb-2">
                        <span className={`mono text-[9px] tracking-[0.16em] ${pv.dirty ? "text-gold" : "text-zinc-500"}`}
                            data-testid="pv-dirty-state">
                            {pv.dirty ? "UNSAVED PREVIEW EDITS" : "CLICK TEXT, CARDS OR IMAGES IN THE PREVIEW"}
                        </span>
                        <div className="flex items-center gap-2">
                            <Btn variant="ghost" onClick={pv.discard} disabled={!pv.dirty} data-testid="pv-discard">
                                <RotateCcw className="w-3.5 h-3.5 mr-1 inline" /> UNDO ALL
                            </Btn>
                            <Btn onClick={pv.save} disabled={!pv.dirty || pv.saving} data-testid="pv-save">
                                <Save className="w-3.5 h-3.5 mr-1 inline" /> {pv.saving ? "SAVING…" : "SAVE EDITS"}
                            </Btn>
                        </div>
                    </div>
                    <ProductLivePreview product={draft} layout={pv.layout} onEdit={pv.onEdit} />
                </div>
            )}
            </div>
            {pv.dialogs}
        </div>
    );
}

// Rating + concern tagging — one place, used by every card, page and the Shop by Concern tabs.
const RatingsConcernsCard = ({ draft, set, editable, saveTab, product }) => {
    const qc = useQueryClient();
    const [newConcern, setNewConcern] = useState("");
    const { data } = useQuery({
        queryKey: ["rhm-concerns"],
        queryFn: async () => (await rhm.get("/concerns")).data,
    });
    const options = data?.options || [];
    const picked = draft.concerns || [];
    const key = (o) => o.toLowerCase().replace(/[^a-z0-9]+/g, "-");

    const saveOptions = async (list, msg) => {
        try {
            await rhm.put("/concerns", { options: list });
            qc.invalidateQueries({ queryKey: ["rhm-concerns"] });
            if (msg) toast.success(msg);
        } catch (e) { toast.error(apiError(e)); }
    };

    const toggle = (label) => set("concerns", picked.includes(label)
        ? picked.filter((c) => c !== label) : [...picked, label]);

    return (
        <Card title="Rating & Concerns" data-testid="ws-rating-concerns"
            action={editable && <Btn onClick={saveTab} data-testid="rhm-save-rating-concerns">SAVE</Btn>}>
            <div className="grid md:grid-cols-3 gap-4">
                <Field label="Rating out of 5" hint="Shows everywhere as 4.5 (210) — customer stars add on top">
                    <Input type="number" step="0.1" min="0" max="5" disabled={!editable}
                        value={draft.rating_manual ?? ""} data-testid="ws-rating-manual"
                        onChange={(e) => set("rating_manual", e.target.value)} />
                </Field>
                <Field label="People who rated" hint="The number shown in brackets">
                    <Input type="number" min="0" disabled={!editable} data-testid="ws-rating-count"
                        value={draft.rating_manual_count ?? ""}
                        onChange={(e) => set("rating_manual_count", e.target.value)} />
                </Field>
                <div className="border border-white/10 p-4">
                    <div className="mono text-[9px] tracking-[0.16em] text-zinc-500">LIVE ON THE WEBSITE</div>
                    <div className="font-display text-2xl mt-1 text-gold" data-testid="ws-rating-live">
                        {Number(product.rating || 0).toFixed(1)}
                        {product.rating_count ? ` (${product.rating_count})` : ""}
                    </div>
                </div>
            </div>

            <div className="mt-6">
                <div className="mono text-[10px] tracking-[0.2em] text-gold mb-3">CONCERNS — SHOP BY CONCERN TABS</div>
                <div className="flex flex-wrap gap-2" data-testid="ws-concerns">
                    {options.map((o) => (
                        <span key={o} className="inline-flex items-center gap-1">
                            <button type="button" disabled={!editable} onClick={() => toggle(o)}
                                data-testid={`ws-concern-${key(o)}`}
                                className={`mono text-[10px] tracking-[0.14em] px-3 py-2 border transition-colors ${
                                    picked.includes(o)
                                        ? "border-lime text-lime bg-lime/10"
                                        : "border-white/15 text-zinc-400 hover:text-white"}`}>
                                {o.toUpperCase()}
                            </button>
                            {editable && (
                                <button type="button" title="Remove from the concern list"
                                    data-testid={`ws-concern-remove-${key(o)}`}
                                    onClick={() => window.confirm(`Remove “${o}” from the concern list?`)
                                        && saveOptions(options.filter((x) => x !== o))}
                                    className="text-zinc-600 hover:text-red-400 text-[10px]">✕</button>
                            )}
                        </span>
                    ))}
                </div>
                {editable && (
                    <div className="mt-4 flex items-end gap-2 max-w-sm">
                        <div className="flex-1">
                            <Field label="Add a new concern">
                                <Input value={newConcern} data-testid="ws-concern-new"
                                    onChange={(e) => setNewConcern(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key !== "Enter" || !newConcern.trim()) return;
                                        saveOptions([...options, newConcern.trim()], `“${newConcern.trim()}” added`);
                                        setNewConcern("");
                                    }} />
                            </Field>
                        </div>
                        <Btn variant="ghost" data-testid="ws-concern-add"
                            onClick={() => {
                                if (!newConcern.trim()) return;
                                saveOptions([...options, newConcern.trim()], `“${newConcern.trim()}” added`);
                                setNewConcern("");
                            }}>ADD</Btn>
                    </div>
                )}
            </div>
        </Card>
    );
};


// Product → Related Blogs (the other half of Blog → Related Products)
const RelatedBlogs = ({ slug }) => {
    const { can } = useRhmAuth();
    const { data: posts = [] } = useQuery({
        queryKey: ["product-blogs", slug],
        queryFn: async () => (await rhm.get(`/blog/for-product/${slug}`)).data,
        enabled: can("blog.view"),
    });
    if (!can("blog.view")) {
        return <Card title="Related articles"><div className="text-sm text-zinc-400">
            You do not have access to the Blog CMS.</div></Card>;
    }
    return (
        <Card title="Related articles" data-testid="product-related-blogs">
            <Table head={["Article", "Status", "Views", ""]}
                empty={!posts.length && <Empty msg="No articles mention this product yet. Attach it from the article editor." />}>
                {posts.map((p) => (
                    <tr key={p.id} data-testid={`product-blog-${p.slug}`}>
                        <Td>
                            <div className="font-semibold">{p.title}</div>
                            <div className="mono text-[9px] tracking-[0.14em] text-zinc-500">/{p.slug}</div>
                        </Td>
                        <Td><Badge tone={p.status === "published" ? "good" : "muted"}>{(p.status || "").toUpperCase()}</Badge></Td>
                        <Td className="text-sm text-zinc-400">{p.views}</Td>
                        <Td>
                            <div className="flex justify-end">
                                <Link to={`/rhm/blog/${p.id}`} className="mono text-[10px] tracking-[0.16em] text-gold">OPEN</Link>
                            </div>
                        </Td>
                    </tr>
                ))}
            </Table>
        </Card>
    );
};


// How this item is listed in the shop: normal product, consultation pack, or a digital ebook/guide.
const ShopListingCard = ({ product, productId, editable }) => {
    const qc = useQueryClient();
    const fileRef = React.useRef(null);
    const [form, setForm] = React.useState({
        shop_type: product.shop_type || "product",
        digital_free: !!product.digital_free,
        sessions_label: product.sessions_label || "",
        consult_area: product.consult_area || "",
    });
    const [busy, setBusy] = React.useState(false);
    const [progress, setProgress] = React.useState(0);

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["rhm-product", productId] });
        qc.invalidateQueries({ queryKey: ["products"] });
    };

    const saveCover = async (urls) => {
        try {
            await rhm.patch(`/products/${productId}`, { images: urls });
            toast.success("Cover updated");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    };

    const save = async () => {
        try {
            await rhm.patch(`/products/${productId}`, form);
            toast.success("Shop listing saved");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    };

    const upload = async (file) => {
        setBusy(true);
        setProgress(0);
        try {
            await uploadDigitalFile(productId, file, setProgress);
            toast.success("File uploaded");
            refresh();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); setProgress(0); }
    };

    const removeFile = async () => {
        if (!window.confirm("Remove the uploaded file?")) return;
        try {
            await rhm.delete(`/digital/${productId}/file`);
            toast.success("File removed");
            refresh();
        } catch (e) { toast.error(apiError(e)); }
    };

    return (
        <Card title="Shop listing" data-testid="shop-listing-card"
            action={editable && <Btn onClick={save} data-testid="shop-listing-save">SAVE</Btn>}>
            <div className="grid md:grid-cols-2 gap-4">
                <Field label="Sell as" hint="Decides which shop section it appears in">
                    <Select value={form.shop_type} disabled={!editable} data-testid="shop-listing-type"
                        onChange={(e) => setForm({ ...form, shop_type: e.target.value })}
                        options={[{ value: "product", label: "Product" },
                            { value: "consultation_pack", label: "Consultation Pack" },
                            { value: "ebook", label: "Ebook / Guide" }]} />
                </Field>
                {form.shop_type === "consultation_pack" && (
                    <>
                        <Field label="Sessions label" hint="Shown on the card, e.g. 3 sessions · 60 min each">
                            <Input value={form.sessions_label} disabled={!editable} data-testid="shop-listing-sessions"
                                onChange={(e) => setForm({ ...form, sessions_label: e.target.value })} />
                        </Field>
                        <Field label="Consultation area key" hint="Used by the Book an appointment button">
                            <Input value={form.consult_area} disabled={!editable} data-testid="shop-listing-area"
                                onChange={(e) => setForm({ ...form, consult_area: e.target.value })} />
                        </Field>
                    </>
                )}
                {form.shop_type === "ebook" && (
                    <>
                        <Field label="Pricing">
                            <label className="flex items-center gap-2 text-sm mt-2">
                                <input type="checkbox" checked={form.digital_free} disabled={!editable}
                                    data-testid="shop-listing-free"
                                    onChange={(e) => setForm({ ...form, digital_free: e.target.checked })} />
                                Free download (no purchase needed)
                            </label>
                        </Field>
                        <div className="md:col-span-2">
                            <CoverImageField urls={product.images || []} disabled={!editable}
                                testId="ebook-cover" label="Cover image (shown on the shop card)"
                                onChange={saveCover} />
                        </div>
                        <Field label={`File (PDF, EPUB or ZIP — up to ${MAX_GUIDE_LABEL})`}
                            hint="Paid guides unlock on the buyer's order page once payment is confirmed">
                            <div className="flex items-center gap-3">
                                <input ref={fileRef} type="file" accept=".pdf,.epub,.zip" hidden
                                    data-testid="shop-listing-file-input"
                                    onChange={(e) => e.target.files[0] && upload(e.target.files[0])} />
                                <Btn variant="ghost" disabled={!editable || busy} data-testid="shop-listing-upload"
                                    onClick={() => fileRef.current?.click()}>
                                    {busy ? `UPLOADING ${progress}%` : product.has_digital_file ? "REPLACE FILE" : "UPLOAD FILE"}
                                </Btn>
                                {product.has_digital_file && (
                                    <>
                                        <span className="text-xs text-zinc-400 truncate">{product.digital_file_name}</span>
                                        <button onClick={removeFile} data-testid="shop-listing-file-remove"
                                            className="mono text-[9px] tracking-[0.14em] text-red-400">REMOVE</button>
                                    </>
                                )}
                            </div>
                        </Field>
                    </>
                )}
            </div>
        </Card>
    );
};
